import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import api from "@/lib/api";

/**
 * Post-checkout landing — polls /billing/status until the user's tier
 * flips to Pro (webhook usually arrives within 2-3 seconds).
 */
export default function BillingSuccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const sid = params.get("session_id");
    const statusHint = params.get("status");
    if (!sid) { navigate("/dashboard"); return; }
    // `status=unknown` means the backend couldn't match this session_id to
    // any pending checkout. Send the user home immediately — polling would
    // just hit the same free-tier status for 20+ seconds.
    if (statusHint === "unknown") {
      toast.error(t("toasts.checkout_canceled"));
      setTimeout(() => navigate("/pricing"), 800);
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get("/billing/status");
        if (cancelled) return;
        setStatus(data);
        if (data.is_pro || tries > 12) {
          if (data.is_pro) toast.success(t("toasts.welcome_pro"));
          setTimeout(() => navigate("/dashboard"), 1200);
          return;
        }
      } catch (err) {
        // Polling: transient network / 401 during warm-up is expected;
        // logged at debug so we don't spam the console every retry.
        console.debug("BillingSuccess: /billing/status poll failed", err);
      }
      tries += 1;
      setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, [navigate, params, t]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="billing-success">
      <div className="max-w-md text-center">
        <div className="inline-flex items-center gap-2 label-eyebrow" style={{ color: "var(--accent-primary)" }}>
          <Sparkles size={14}/> {t("billing_success.title")}
        </div>
        <h1
          className="mt-4 text-3xl font-medium tracking-tight"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {t("common.loading")}
        </h1>
        <p className="mt-3 text-sm text-[var(--text-secondary)]">
          {t("billing_success.subtitle")}
        </p>
        {status && (
          <div className="mt-6 text-xs text-[var(--text-muted)] font-mono">
            tier: {status.tier} · status: {status.status || "…"}
          </div>
        )}
      </div>
    </div>
  );
}
